/**
 * Client service for sector operations schedules and due instances.
 * R39 A5.a (plan docs/plans/sector-operations.md §2 SO-D10, SO-P7, SO-P61; §4 ops_schedules, ops_due; §6 A5.a).
 * Wraps callables for writes and listens to firestore collections for reads.
 */

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  OpsSector,
  OpsSchedule,
  OpsScheduleStatus,
  OpsDue,
  OpsDueStatus,
  CreateScheduleInput,
  UpdateScheduleInput,
  PauseScheduleInput,
  ResumeScheduleInput,
  ArchiveScheduleInput,
  SkipDueInput,
  RecordCheckpointScanInput,
  RecordCheckpointScanResult,
} from '../types/ops'
import type { OpsSubscriptionAccessOptions } from './opsRostersService'

// ---------------------------------------------------------------------------
// Callables (Writes)
// ---------------------------------------------------------------------------

export async function createSchedule(input: CreateScheduleInput): Promise<{
  scheduleId: string
  status: OpsScheduleStatus
  replayed?: boolean
}> {
  return callable<
    CreateScheduleInput,
    { scheduleId: string; status: OpsScheduleStatus; replayed?: boolean }
  >('createSchedule', input)
}

export async function updateSchedule(input: UpdateScheduleInput): Promise<{
  scheduleId: string
  status: OpsScheduleStatus
  deletedDues: number
  replayed?: boolean
}> {
  return callable<
    UpdateScheduleInput,
    { scheduleId: string; status: OpsScheduleStatus; deletedDues: number; replayed?: boolean }
  >('updateSchedule', input)
}

export async function pauseSchedule(input: PauseScheduleInput): Promise<{
  scheduleId: string
  status: OpsScheduleStatus
  deletedDues: number
  replayed?: boolean
}> {
  return callable<
    PauseScheduleInput,
    { scheduleId: string; status: OpsScheduleStatus; deletedDues: number; replayed?: boolean }
  >('pauseSchedule', input)
}

export async function resumeSchedule(input: ResumeScheduleInput): Promise<{
  scheduleId: string
  status: OpsScheduleStatus
  nextGenAt: number
  replayed?: boolean
}> {
  return callable<
    ResumeScheduleInput,
    { scheduleId: string; status: OpsScheduleStatus; nextGenAt: number; replayed?: boolean }
  >('resumeSchedule', input)
}

export async function archiveSchedule(input: ArchiveScheduleInput): Promise<{
  scheduleId: string
  status: OpsScheduleStatus
  deletedDues: number
  replayed?: boolean
}> {
  return callable<
    ArchiveScheduleInput,
    { scheduleId: string; status: OpsScheduleStatus; deletedDues: number; replayed?: boolean }
  >('archiveSchedule', input)
}

export async function skipDue(input: SkipDueInput): Promise<{
  dueId: string
  status: OpsDueStatus
  replayed?: boolean
}> {
  return callable<
    SkipDueInput,
    { dueId: string; status: OpsDueStatus; replayed?: boolean }
  >('skipDue', input)
}

export async function recordCheckpointScan(
  input: RecordCheckpointScanInput,
): Promise<RecordCheckpointScanResult> {
  return callable<RecordCheckpointScanInput, RecordCheckpointScanResult>(
    'recordCheckpointScan',
    input,
  )
}

// ---------------------------------------------------------------------------
// Subscriptions (Reads)
// ---------------------------------------------------------------------------

export interface SubscribeSchedulesFilters {
  sector?: OpsSector
  status?: OpsScheduleStatus
}

export function subscribeSchedules(
  workspaceId: string,
  filters: SubscribeSchedulesFilters | undefined,
  callback: (schedules: OpsSchedule[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_schedules')
  const constraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (filters?.sector) {
    constraints.push(where('sector', '==', filters.sector))
  }
  if (filters?.status) {
    constraints.push(where('status', '==', filters.status))
  }
  const q = query(coll, ...constraints)
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsSchedule))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export interface SubscribeDuesOptions extends OpsSubscriptionAccessOptions {
  workspaceId?: string
  placeIds?: string[]
  status?: OpsDueStatus
  from: number
  to: number
  callerUid: string
}

function isWorkspaceWideScope(opts: OpsSubscriptionAccessOptions | undefined): boolean {
  if (opts?.isRestricted === true) return false
  return (
    opts?.isElevated === true ||
    opts?.isManager === true ||
    opts?.isRestricted === false
  )
}

function buildPlaceFilterConstraint(placeIds: string[] | undefined): QueryConstraint | null {
  if (!placeIds || placeIds.length === 0) return null
  const cleaned = Array.from(new Set(placeIds.map((p) => p.trim()).filter(Boolean)))
  if (cleaned.length === 0) return null
  if (cleaned.length === 1) {
    return where('placeId', '==', cleaned[0])
  }
  return where('placeId', 'in', cleaned.slice(0, 30))
}

export function subscribeDues(
  workspaceIdOrOptions: string | SubscribeDuesOptions,
  optionsOrCallback: SubscribeDuesOptions | ((dues: OpsDue[]) => void),
  callbackOrOnError?: ((dues: OpsDue[]) => void) | ((error: Error) => void),
  maybeOnError?: (error: Error) => void,
): Unsubscribe {
  let workspaceId: string
  let options: SubscribeDuesOptions
  let callback: (dues: OpsDue[]) => void
  let onError: ((error: Error) => void) | undefined

  if (typeof workspaceIdOrOptions === 'string') {
    workspaceId = workspaceIdOrOptions
    options = optionsOrCallback as SubscribeDuesOptions
    callback = callbackOrOnError as (dues: OpsDue[]) => void
    onError = maybeOnError
  } else {
    options = workspaceIdOrOptions
    workspaceId = options.workspaceId ?? ''
    callback = optionsOrCallback as (dues: OpsDue[]) => void
    onError = callbackOrOnError as ((error: Error) => void) | undefined
  }

  if (!workspaceId) {
    callback([])
    return () => undefined
  }

  const callerUid = options?.callerUid?.trim()
  if (!callerUid) {
    onError?.(new Error('callerUid is required for subscribeDues'))
    return () => undefined
  }

  if (
    typeof options.from !== 'number' ||
    !Number.isFinite(options.from) ||
    typeof options.to !== 'number' ||
    !Number.isFinite(options.to)
  ) {
    onError?.(new Error('Valid from and to timestamps are required for subscribeDues'))
    return () => undefined
  }

  const coll = collection(firestoreDb, 'ops_due')
  const placeConstraint = buildPlaceFilterConstraint(options.placeIds)

  if (!isWorkspaceWideScope(options)) {
    const qNullConstraints: QueryConstraint[] = [
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
    ]
    const qUidConstraints: QueryConstraint[] = [
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
    ]
    if (placeConstraint) {
      qNullConstraints.push(placeConstraint)
      qUidConstraints.push(placeConstraint)
    }
    if (options.status) {
      qNullConstraints.push(where('status', '==', options.status))
      qUidConstraints.push(where('status', '==', options.status))
    }
    qNullConstraints.push(
      where('windowStart', '>=', options.from),
      where('windowStart', '<=', options.to),
      orderBy('windowStart', 'asc'),
    )
    qUidConstraints.push(
      where('windowStart', '>=', options.from),
      where('windowStart', '<=', options.to),
      orderBy('windowStart', 'asc'),
    )

    const qNull = query(coll, ...qNullConstraints)
    const qUid = query(coll, ...qUidConstraints)

    let docsNull: OpsDue[] = []
    let docsUid: OpsDue[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsDue>()
      for (const d of docsNull) map.set(d.id, d)
      for (const d of docsUid) map.set(d.id, d)
      const merged = Array.from(map.values())
      merged.sort((a, b) => a.windowStart - b.windowStart || a.id.localeCompare(b.id))
      callback(merged)
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        docsNull = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsDue)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        docsUid = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsDue)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const constraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (placeConstraint) {
    constraints.push(placeConstraint)
  }
  if (options.status) {
    constraints.push(where('status', '==', options.status))
  }
  constraints.push(
    where('windowStart', '>=', options.from),
    where('windowStart', '<=', options.to),
    orderBy('windowStart', 'asc'),
  )

  const q = query(coll, ...constraints)
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsDue))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}
